import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import ProductPicker from '../components/ProductPicker';
import WizardShell from '../components/WizardShell';
import MobileApplyIntro from '../components/MobileApplyIntro';
import MobileSlider from '../components/MobileSlider';
import { useYTunnusLookup } from '../hooks/useYTunnusLookup';
import { useLoanApplication } from '../hooks/useLoanApplication';
import { useFactoringApplication } from '../hooks/useFactoringApplication';
import {
  calculateMonthlyPayment,
  LOAN_AMOUNT_DEFAULT,
  LOAN_AMOUNT_MAX,
  LOAN_AMOUNT_MIN,
  LOAN_AMOUNT_STEP,
  LOAN_DURATION_DEFAULT,
  LOAN_DURATION_MAX,
  LOAN_DURATION_MIN,
  LOAN_DURATION_STEP,
  FACTORING_AMOUNT_DEFAULT,
  FACTORING_AMOUNT_MAX,
  FACTORING_AMOUNT_MIN,
  FACTORING_AMOUNT_STEP,
} from '../utils/loanCalculations';
import {
  clearMobileApplicationDraft,
  loadMobileApplicationDraft,
  markTermsOpenedFromApplication,
  saveMobileApplicationDraft,
} from '../utils/mobileApplicationDraft';

const TOTAL_STEPS = 4;

const initialShared = {
  yTunnus: '',
  email: '',
  puhelinnumero: '',
  companyName: '',
  address: '',
  website: '',
  hyvaksyKayttoehdot: false,
};

const initialLoan = {
  lainamaara: LOAN_AMOUNT_DEFAULT,
  lainaAika: LOAN_DURATION_DEFAULT,
  korvausNykyisestaRahoituksesta: false,
};

const initialFactoring = {
  laskumaara: FACTORING_AMOUNT_DEFAULT,
  korvausNykyisestaRahoituksesta: false,
};

const stepTitles = {
  loan: ['Y-tunnus', 'Yrityksen tiedot', 'Lainan tiedot', 'Yhteystiedot ja yhteenveto'],
  factoring: ['Y-tunnus', 'Yrityksen tiedot', 'Laskurahoitus', 'Yhteystiedot ja yhteenveto'],
};

function readDraftState() {
  const draft = loadMobileApplicationDraft();
  if (!draft) return null;
  return {
    product: draft.product === 'factoring' ? 'factoring' : 'loan',
    step: typeof draft.step === 'number' ? draft.step : 1,
    shared: { ...initialShared, ...draft.shared },
    loanFields: { ...initialLoan, ...draft.loanFields },
    factoringFields: { ...initialFactoring, ...draft.factoringFields },
  };
}

export default function MobileApplication() {
  const [product, setProduct] = useState(() => readDraftState()?.product ?? 'loan');
  const [step, setStep] = useState(() => readDraftState()?.step ?? 1);
  const [shared, setShared] = useState(() => readDraftState()?.shared ?? initialShared);
  const [loanFields, setLoanFields] = useState(() => readDraftState()?.loanFields ?? initialLoan);
  const [factoringFields, setFactoringFields] = useState(
    () => readDraftState()?.factoringFields ?? initialFactoring
  );
  const [stepError, setStepError] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { lookup, loading: ytjLoading, error: ytjError, setError: setYtjError, validateYTunnus } =
    useYTunnusLookup();
  const loanApp = useLoanApplication();
  const factoringApp = useFactoringApplication();

  const monthlyPayment = useMemo(
    () => calculateMonthlyPayment(loanFields.lainamaara, loanFields.lainaAika),
    [loanFields.lainamaara, loanFields.lainaAika]
  );

  useEffect(() => {
    saveMobileApplicationDraft({
      product,
      step,
      shared,
      loanFields,
      factoringFields,
    });
  }, [product, step, shared, loanFields, factoringFields]);

  useEffect(() => {
    const returnStep = location.state?.returnStep;
    if (typeof returnStep !== 'number' || returnStep < 1 || returnStep > TOTAL_STEPS) {
      return;
    }

    const draft = loadMobileApplicationDraft();
    if (draft?.shared) {
      setShared((current) => ({ ...current, ...draft.shared }));
    }
    setStep(returnStep);
    setStepError(null);
    navigate('/m', { replace: true, state: {} });
  }, [location.state, navigate]);

  const persistDraft = () => {
    saveMobileApplicationDraft({
      product,
      step,
      shared,
      loanFields,
      factoringFields,
    });
    markTermsOpenedFromApplication();
  };

  const handleProductChange = (next) => {
    if (next === product) return;
    setProduct(next);
    setStep(1);
    setStepError(null);
    setYtjError(null);
  };

  const handleYTunnusChange = async (value) => {
    setShared((s) => ({ ...s, yTunnus: value }));
    setStepError(null);
    if (validateYTunnus(value)) {
      const company = await lookup(value);
      if (company) {
        setShared((s) => ({
          ...s,
          companyName: company.companyName,
          address: company.address,
          website: company.website,
        }));
      } else {
        setShared((s) => ({ ...s, companyName: '', address: '', website: '' }));
      }
    }
  };

  const validateStep = () => {
    setStepError(null);
    if (step === 1) {
      if (!validateYTunnus(shared.yTunnus)) {
        setStepError('Syötä kelvollinen Y-tunnus (1234567-8).');
        return false;
      }
      if (ytjLoading) {
        setStepError('Odota hetki, haetaan yrityksen tietoja.');
        return false;
      }
      return true;
    }
    if (step === 2) {
      if (!shared.companyName) {
        setStepError('Yrityksen tietoja ei löytynyt. Tarkista Y-tunnus.');
        return false;
      }
      return true;
    }
    if (step === 3) return true;
    if (step === 4) {
      if (!shared.email?.trim()) {
        setStepError('Sähköposti on pakollinen.');
        return false;
      }
      if (!shared.puhelinnumero?.trim()) {
        setStepError('Puhelinnumero on pakollinen.');
        return false;
      }
      if (!shared.hyvaksyKayttoehdot) {
        setStepError('Hyväksy käyttöehdot jatkaaksesi.');
        return false;
      }
      return true;
    }
    return true;
  };

  const handleNext = async () => {
    if (!validateStep()) return;

    if (step < TOTAL_STEPS) {
      setStep((s) => s + 1);
      return;
    }

    const payload = {
      ...shared,
      ...(product === 'loan' ? loanFields : factoringFields),
    };

    const result =
      product === 'loan' ? await loanApp.submit(payload) : await factoringApp.submit(payload);

    if (result?.id && result?.trackingToken) {
      clearMobileApplicationDraft();
      const thanksPath =
        product === 'loan' ? '/m/kiitos-yrityslaina' : '/m/kiitos-laskurahoitus';
      navigate(thanksPath, {
        state: {
          trackingToken: result.trackingToken,
        },
      });
    } else {
      setStepError(product === 'loan' ? loanApp.error : factoringApp.error);
    }
  };

  const handleBack = () => {
    setStepError(null);
    setStep((s) => Math.max(1, s - 1));
  };

  const displayError = stepError || ytjError;
  const submitting = loanApp.submitting || factoringApp.submitting;
  const titles = stepTitles[product];

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <>
          <MobileApplyIntro product={product} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            Aloita syöttämällä yrityksesi Y-tunnus.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Haemme tiedot automaattisesti Patentti- ja rekisterihallituksen YTJ-rekisteristä.
          </Typography>
          <TextField
            label="Y-tunnus"
            value={shared.yTunnus}
            onChange={(e) => handleYTunnusChange(e.target.value)}
            placeholder="1234567-8"
            fullWidth
            required
            autoComplete="off"
          />
          {ytjLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Haetaan yrityksen tietoja…</Typography>
            </Box>
          )}
        </>
      );
    }

    if (step === 2) {
      return (
        <>
          <TextField
            label="Yrityksen nimi"
            value={shared.companyName}
            fullWidth
            disabled
            sx={{ mb: 2 }}
          />
          <TextField label="Osoite" value={shared.address} fullWidth disabled sx={{ mb: 2 }} />
          {shared.website && (
            <TextField label="Verkkosivusto" value={shared.website} fullWidth disabled />
          )}
        </>
      );
    }

    if (step === 3) {
      if (product === 'loan') {
        return (
          <>
            <MobileSlider
              label="Lainamäärä"
              value={loanFields.lainamaara}
              onChange={(v) => setLoanFields((f) => ({ ...f, lainamaara: v }))}
              min={LOAN_AMOUNT_MIN}
              max={LOAN_AMOUNT_MAX}
              step={LOAN_AMOUNT_STEP}
              formatValue={(v) => `${v.toLocaleString('fi-FI')} €`}
            />
            <MobileSlider
              label="Laina-aika"
              value={loanFields.lainaAika}
              onChange={(v) => setLoanFields((f) => ({ ...f, lainaAika: v }))}
              min={LOAN_DURATION_MIN}
              max={LOAN_DURATION_MAX}
              step={LOAN_DURATION_STEP}
              formatValue={(v) => `${v} kk`}
            />
            <Box
              sx={{
                mt: 1,
                p: 2,
                bgcolor: 'mobile.highlight',
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Arvioitu kuukausierä *
              </Typography>
              <Typography variant="h2" sx={{ fontSize: '1.5rem' }}>
                {Number(monthlyPayment).toLocaleString('fi-FI', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                €
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: '0.75rem' }}>
              * Suuntaa-antava arvio. Lopullinen tarjous riippuu rahoittajasta.
            </Typography>
          </>
        );
      }

      return (
        <>
          <MobileSlider
            label="Arvioitu laskumäärä"
            value={factoringFields.laskumaara}
            onChange={(v) => setFactoringFields((f) => ({ ...f, laskumaara: v }))}
            min={FACTORING_AMOUNT_MIN}
            max={FACTORING_AMOUNT_MAX}
            step={FACTORING_AMOUNT_STEP}
            formatValue={(v) => `${v.toLocaleString('fi-FI')} €`}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Factoring muuttaa myyntilaskut rahaksi ennen eräpäivää — ilman kuukausieriä.
          </Typography>
        </>
      );
    }

    const amountLabel =
      product === 'loan'
        ? `${loanFields.lainamaara.toLocaleString('fi-FI')} € / ${loanFields.lainaAika} kk`
        : `${factoringFields.laskumaara.toLocaleString('fi-FI')} €`;

    return (
      <>
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: 'mobile.highlight',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Yhteenveto
          </Typography>
          <Typography variant="body1">
            <strong>{shared.companyName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {shared.yTunnus}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {product === 'loan' ? 'Yrityslaina' : 'Laskurahoitus'}: {amountLabel}
          </Typography>
        </Box>

        <TextField
          label="Sähköposti"
          type="email"
          value={shared.email}
          onChange={(e) => setShared((s) => ({ ...s, email: e.target.value }))}
          placeholder="esimerkki@yritys.fi"
          fullWidth
          required
          sx={{ mb: 2 }}
        />
        <TextField
          label="Puhelinnumero"
          value={shared.puhelinnumero}
          onChange={(e) => setShared((s) => ({ ...s, puhelinnumero: e.target.value }))}
          placeholder="0401234567"
          fullWidth
          required
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={shared.hyvaksyKayttoehdot}
              onChange={(e) =>
                setShared((s) => ({ ...s, hyvaksyKayttoehdot: e.target.checked }))
              }
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              Olen lukenut ja hyväksyn{' '}
              <Link
                component={RouterLink}
                to="/m/kayttoehdot"
                state={{ fromApplication: true }}
                onClick={persistDraft}
                underline="hover"
              >
                käyttöehdot
              </Link>
              *
            </Typography>
          }
        />
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>Hakemus | Rahoituskanava</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <ProductPicker product={product} onChange={handleProductChange} locked={step > 1} />
        <WizardShell
        step={step}
        totalSteps={TOTAL_STEPS}
        stepTitle={titles[step - 1]}
        onBack={handleBack}
        onNext={handleNext}
        nextLabel={step === TOTAL_STEPS ? 'Lähetä hakemus' : 'Seuraava'}
        loading={submitting}
        error={displayError}
      >
        {renderStepContent()}
        </WizardShell>
      </Box>
    </>
  );
}
